import React from 'react';
import { Link } from 'react-router-dom';
import { LEGAL_EFFECTIVE_DATE, LEGAL_CONTACT_EMAIL } from '../../config/legal';

// Starter Privacy Policy. Describes honestly what Pinion collects and why,
// written to be GDPR-aware without over-promising. To be reviewed by counsel
// once traffic warrants it. Keep the version/date in sync with src/config/legal.ts.
export const Privacy: React.FC = () => {
  return (
    <div className="text-white max-w-app-middle-side mx-auto w-full px-app-base py-app-xl leading-relaxed">
      <h1 className="text-app-3xl font-bold">Privacy Policy</h1>
      <p className="text-app-sm text-app-gray mt-app-xs">Effective {LEGAL_EFFECTIVE_DATE}</p>

      <p className="text-app-gray-light mt-app-lg">
        This Privacy Policy explains what information <span className="text-app-primary font-semibold">Pinion</span>{' '}
        collects, why, and what choices you have. We aim to collect only what we need to run the
        Service.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">1. Information we collect</h2>
      <ul className="mt-app-base flex flex-col gap-app-base list-disc pl-app-lg text-app-gray-light">
        <li>
          <span className="text-white font-semibold">Account information</span> — your name, email
          address, and a securely hashed password. We never store your password in plain text.
        </li>
        <li>
          <span className="text-white font-semibold">Content you create</span> — the Pinions you post,
          your votes, comments, bookmarks, and profile details. Votes and aggregate results are public
          by design.
        </li>
        <li>
          <span className="text-white font-semibold">Technical information</span> — basic log and
          device data such as IP address, browser type, and pages visited, used to keep the Service
          secure and working.
        </li>
      </ul>

      <h2 className="text-app-xl font-bold mt-app-2xl">2. How we use your information</h2>
      <ul className="mt-app-base flex flex-col gap-app-base list-disc pl-app-lg text-app-gray-light">
        <li>To provide and operate the Service — accounts, voting, comments, and soulmate matching.</li>
        <li>To keep Pinion safe — preventing abuse, fraud, and vote manipulation.</li>
        <li>To diagnose and fix problems and improve the Service.</li>
        <li>To contact you about important changes to the Service or these policies.</li>
      </ul>

      <h2 className="text-app-xl font-bold mt-app-2xl">3. Legal bases (where GDPR applies)</h2>
      <p className="text-app-gray-light mt-app-base">
        Where the EU/EEA General Data Protection Regulation applies, we rely on: performance of our
        contract with you (to run your account and the Service), our legitimate interests (keeping the
        Service secure and improving it), and your consent (which you give at signup and can withdraw).
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">4. Cookies and local storage</h2>
      <p className="text-app-gray-light mt-app-base">
        We use cookies and your browser&rsquo;s local storage for essential purposes such as keeping you
        signed in and remembering preferences. We do not use advertising-tracking cookies.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">5. Service providers</h2>
      <p className="text-app-gray-light mt-app-base">
        We share limited data with trusted providers who help us run Pinion, under agreements that
        restrict their use of it. These currently include hosting and content-delivery providers
        (including Cloudflare), our database and image-storage hosting, and an error-monitoring provider
        (Sentry) that helps us detect crashes. Our error monitoring is configured to limit the
        personal data it captures: session-replay recordings mask the text you type, including email
        and password fields, and we do not send the contents of sign-in or registration requests.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">6. Retention</h2>
      <p className="text-app-gray-light mt-app-base">
        We keep your account information for as long as your account is active. If you delete your
        account, we remove or anonymise your personal information within a reasonable period, except
        where we must keep it to meet legal obligations or resolve disputes.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">7. Your rights</h2>
      <p className="text-app-gray-light mt-app-base">
        Depending on where you live, you may have the right to access, correct, export, or delete your
        personal data, and to object to or restrict certain processing. To exercise these rights,
        contact us at the address below. You can also withdraw consent at any time by closing your
        account.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">8. International transfers</h2>
      <p className="text-app-gray-light mt-app-base">
        Pinion operates globally, so your information may be processed in countries other than your own.
        Where required, we use appropriate safeguards for such transfers.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">9. Children</h2>
      <p className="text-app-gray-light mt-app-base">
        Pinion is not intended for children under 13, and we do not knowingly collect their personal
        information. If you believe a child has given us personal data, contact us and we will remove it.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">10. Security</h2>
      <p className="text-app-gray-light mt-app-base">
        We take reasonable technical and organisational measures to protect your data, including hashed
        passwords and encrypted connections. No service can be perfectly secure, but we work to keep your
        information safe.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">11. Changes to this policy</h2>
      <p className="text-app-gray-light mt-app-base">
        We may update this Privacy Policy from time to time. If we make material changes we will update
        the effective date above and, where appropriate, ask you to review the new version.
      </p>

      <h2 className="text-app-xl font-bold mt-app-2xl">12. Contact</h2>
      <p className="text-app-gray-light mt-app-base">
        Questions about your privacy? Reach us at{' '}
        <a href={`mailto:${LEGAL_CONTACT_EMAIL}`} className="text-app-primary underline">
          {LEGAL_CONTACT_EMAIL}
        </a>
        .
      </p>

      <p className="text-app-gray-light mt-app-2xl">
        See also our{' '}
        <Link to="/terms" className="text-app-primary underline">Terms of Service</Link>.
      </p>
    </div>
  );
};
